import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// ============================================================================
// REVAMP: ephemeral, per-tab session store.
//
// The backend no longer saves anything. To still let users move between the
// steps of a module (e.g. generate flashcards -> revise them) during a visit,
// we keep the current results in the browser via sessionStorage, which is
// cleared when the tab closes. Nothing is sent to or persisted on the server.
// ============================================================================

interface SessionStore {
  [moduleKey: string]: unknown;
}

interface ClientSessionContextType {
  get: <T = unknown>(key: string) => T | null;
  set: (key: string, value: unknown) => void;
  remove: (key: string) => void;
  clear: () => void;
}

const STORAGE_PREFIX = 'studymind_session_';
const ClientSessionContext = createContext<ClientSessionContextType | undefined>(undefined);

export const ClientSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [store, setStore] = useState<SessionStore>(() => {
    const initial: SessionStore = {};
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const fullKey = sessionStorage.key(i);
        if (fullKey && fullKey.startsWith(STORAGE_PREFIX)) {
          const raw = sessionStorage.getItem(fullKey);
          if (raw) initial[fullKey.slice(STORAGE_PREFIX.length)] = JSON.parse(raw);
        }
      }
    } catch {
      // sessionStorage unavailable (private mode etc.) — fall back to memory.
    }
    return initial;
  });

  useEffect(() => {
    try {
      Object.entries(store).forEach(([k, v]) =>
        sessionStorage.setItem(STORAGE_PREFIX + k, JSON.stringify(v))
      );
    } catch {
      // ignore quota / availability errors
    }
  }, [store]);

  const value: ClientSessionContextType = {
    get: <T,>(key: string) => (store[key] as T) ?? null,
    set: (key, val) => setStore((prev) => ({ ...prev, [key]: val })),
    remove: (key) =>
      setStore((prev) => {
        const next = { ...prev };
        delete next[key];
        try {
          sessionStorage.removeItem(STORAGE_PREFIX + key);
        } catch {
          /* ignore */
        }
        return next;
      }),
    clear: () => {
      try {
        Object.keys(store).forEach((k) => sessionStorage.removeItem(STORAGE_PREFIX + k));
      } catch {
        /* ignore */
      }
      setStore({});
    },
  };

  return <ClientSessionContext.Provider value={value}>{children}</ClientSessionContext.Provider>;
};

export const useClientSession = (): ClientSessionContextType => {
  const ctx = useContext(ClientSessionContext);
  if (!ctx) throw new Error('useClientSession must be used within a ClientSessionProvider');
  return ctx;
};
